/**
 * Displays the currently selected color details.
 */
import {
	circleContainerSx,
	colorSelectedContainerSx,
	youColorCircleStateSx,
	yourColorBoxSx,
	yourColorCircleSx,
	yourColorEditIconSx,
	yourColorNameContainerBoxSx,
	yourColorNameContainerSx,
	yourColorNameEllipsisSx,
	yourColorTypographySx,
} from '@/components/blocks/ProductDetails/styles/colorSelected';
import { capitalizeFirstLetter } from '@/components/blocks/ProductDetails/utils/utils';
import {
	DEEP_BASE_COLOR_NAME,
	MEDIUM_BASE_COLOR_NAME,
	NO_RANGE_INDICATOR,
	NOT_APPLICABLE,
	WHITE_BASE_COLOR_NAME,
} from '@/components/content/ProductDetails/constants';
import DynamicIcon from '@/components/icons/DynamicIcon';
import { useLocalization } from '@/data/Localization';
import { useColorDrawerState } from '@/data/state/useColorDrawerState';
import { LineRangeColor, PaintColor } from '@/data/types/ColorType';
import { Box, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { FC, useCallback, useMemo } from 'react';
import {
	MATERIAL_SYMBOL_ICONS,
	OpenSansFontWeights,
	PASTELES_FAMILY_COLOR,
} from 'shared/custom/constants/commonConstants';

interface ColorSelectedProps {
	isDrawer?: boolean;
	allLineColors: LineRangeColor[] | undefined;
}

export const ColorSelected: FC<ColorSelectedProps> = (props) => {
	const { isDrawer = false, allLineColors } = props;

	const { BuyByColor } = useLocalization('HDMProductDetail');
	const { actions: colorDrawerActions, colorDrawerState } = useColorDrawerState();
	const { colorCodeSelected, colorSelectedDrawer } = colorDrawerState;
	let colorSelected: PaintColor | null = null;
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));

	if (isDrawer && colorSelectedDrawer) {
		colorSelected = colorSelectedDrawer;
	} else {
		colorSelected = colorCodeSelected;
	}
	const isBaseTransparent =
		((colorSelected?.base === MEDIUM_BASE_COLOR_NAME ||
			colorSelected?.base === DEEP_BASE_COLOR_NAME) &&
			colorSelected.code === NOT_APPLICABLE) ||
		false;

	const { hasCode, colorRgb } = useMemo(() => {
		const code = colorSelected?.code;
		return {
			hasCode: code !== undefined && code !== NOT_APPLICABLE,
			isTransparentBase: colorSelected?.base !== WHITE_BASE_COLOR_NAME && code === NOT_APPLICABLE,
			canModify: !isDrawer && code !== NOT_APPLICABLE,
			colorRgb: `rgb(${colorSelected?.R ?? 255}, ${colorSelected?.G ?? 255}, ${
				colorSelected?.B ?? 255
			})`,
		};
	}, [colorSelected, isDrawer]);

	const handleOpenBuyByColor = useCallback(() => {
		let range: string | null = null;
		if (colorSelected?.range && colorSelected?.range !== NO_RANGE_INDICATOR) {
			range = colorSelected.range;
		} else {
			range = PASTELES_FAMILY_COLOR;
		}
		const selectedRangeColors = allLineColors?.filter((lineColor) => lineColor.range === range)[0];
		colorDrawerActions.setColorDrawerData({
			isOpen: true,
			changeBaseColor: undefined,
			pendingRange: range,
			rangeColors: selectedRangeColors?.colors,
		});
	}, [allLineColors, colorDrawerActions, colorSelected?.range]);

	return !isBaseTransparent ? (
		<Box sx={colorSelectedContainerSx({ isDrawer, isMobile })}>
			<Box sx={yourColorBoxSx}>
				<Typography
					variant="body1"
					fontWeight={OpenSansFontWeights.semibold}
					sx={yourColorTypographySx}
				>
					{!isDrawer ? BuyByColor.labels.yourColor.t() : BuyByColor.labels.yourSelectedColor.t()}
				</Typography>
			</Box>
			<Box sx={circleContainerSx}>
				<Box sx={yourColorCircleSx({ colorRgb, isMobile })}>
					{!isMobile ? (
						<Box sx={youColorCircleStateSx} onClick={handleOpenBuyByColor}>
							<DynamicIcon
								name={MATERIAL_SYMBOL_ICONS.editSquare}
								size={20}
								className="hover-icon"
							/>
						</Box>
					) : null}
				</Box>
				<Box sx={yourColorNameContainerSx}>
					{colorSelected?.name && (
						<Box sx={yourColorNameContainerBoxSx}>
							<Typography sx={{ mr: '1px' }} variant="caption" component="span" color="grey.500">
								{BuyByColor.labels.colorName.t()}:
							</Typography>
							<Typography
								variant="caption"
								component="span"
								color="grey.500"
								fontWeight={OpenSansFontWeights.bold}
								sx={yourColorNameEllipsisSx}
							>
								{capitalizeFirstLetter(colorSelected?.name)}
							</Typography>
						</Box>
					)}
					{colorSelected?.code && colorSelected?.code !== NOT_APPLICABLE && (
						<Box sx={yourColorNameContainerBoxSx}>
							<Typography sx={{ mr: '1px' }} variant="caption" component="span" color="grey.500">
								{BuyByColor.labels.code.t()}:
							</Typography>
							<Typography
								variant="caption"
								component="span"
								color="grey.500"
								fontWeight={OpenSansFontWeights.bold}
								sx={yourColorNameEllipsisSx}
							>
								{colorSelected?.code}
							</Typography>
						</Box>
					)}
				</Box>
				{isMobile ? (
					<IconButton disableRipple sx={yourColorEditIconSx} onClick={handleOpenBuyByColor}>
						<DynamicIcon name={MATERIAL_SYMBOL_ICONS.editSquare} size={20} className="hover-icon" />
					</IconButton>
				) : null}
			</Box>
		</Box>
	) : null;
};
